import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Edit, Trash2, Settings, Eye, EyeOff, Plus, UserCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';

const RafflesList = () => {
  const navigate = useNavigate();
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRaffles();
  }, []);

  const fetchRaffles = async () => {
    try {
      const response = await axios.get(`${API}/raffles/my-raffles`);
      setRaffles(response.data);
    } catch (error) {
      console.error('Error fetching raffles:', error);
      toast.error('Error al cargar rifas');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (raffleId, currentStatus) => {
    try {
      await axios.put(`${API}/raffles/${raffleId}`, {
        is_active: !currentStatus
      });
      toast.success(currentStatus ? 'Rifa desactivada' : 'Rifa activada');
      fetchRaffles();
    } catch (error) {
      console.error('Error toggling raffle:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (raffleId) => {
    try {
      await axios.delete(`${API}/raffles/${raffleId}`);
      toast.success('Rifa eliminada');
      fetchRaffles();
    } catch (error) {
      console.error('Error deleting raffle:', error);
      toast.error('Error al eliminar rifa');
    }
  };

  const getRaffleTypeLabel = (type) => {
    switch (type) {
      case 'classic':
        return 'Clásica (00-99)';
      case 'lines':
        return '200 Líneas x 5';
      case 'lines_125':
        return '125 Líneas x 8';
      case 'lines_250':
        return '250 filas Manu';
      case 'lines_250_osvaldo':
        return '250 Líneas Osvaldo';
      case 'lines_50':
        return '50 Líneas x 2';
      case 'lines_500':
        return '500 Líneas x 2';
      case 'lines_20':
        return '20 Líneas x 5';
      case 'custom':
        return 'Personalizada';
      case 'clientes':
        return 'Rifa para Clientes';
      default:
        return type;
    }
  };

  const getStats = (raffle) => raffle.stats || { available: 0, reserved: 0, paid: 0 };

  if (loading) {
    return <div className="text-center py-12">Cargando rifas...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h2 className="text-3xl font-bold" data-testid="raffles-list-title">Mis Rifas</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => navigate('/admin/rifas/create-clientes')}
            className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
          >
            <UserCircle className="mr-2" size={20} />
            Rifa para Clientes
          </Button>
          <Button
            onClick={() => navigate('/admin/create')}
            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
          >
            <Plus className="mr-2" size={20} />
            Crear Rifa
          </Button>
        </div>
      </div>

      {raffles.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">
              No tenés rifas creadas todavía
            </p>
            <Button
              onClick={() => navigate('/admin/create')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Crear Mi Primera Rifa
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {raffles.map((raffle) => {
            const stats = getStats(raffle);
            return (
              <Card key={raffle.id} className="hover:shadow-lg transition-shadow" data-testid={`raffle-card-${raffle.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{raffle.title}</CardTitle>
                      <CardDescription>
                        <Badge variant={raffle.is_active ? 'default' : 'secondary'}>
                          {raffle.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                        <Badge variant="outline" className="ml-2">
                          {getRaffleTypeLabel(raffle.raffle_type)}
                        </Badge>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Precio unitario:</p>
                    <p className="text-xl font-bold text-green-600">${raffle.unit_price}</p>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-600">✅ Disponibles:</span>
                      <span className="font-semibold">{stats.available}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-amber-600">⌛ Reservados:</span>
                      <span className="font-semibold">{stats.reserved}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-600">💰 Pagados:</span>
                      <span className="font-semibold">{stats.paid}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col gap-2">
                  <Button
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={() => navigate(`/admin/manage/${raffle.id}`)}
                    data-testid={`manage-raffle-${raffle.id}`}
                  >
                    <Settings className="mr-2" size={18} />
                    Gestionar
                  </Button>
                  <div className="flex gap-2 w-full">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => navigate(`/admin/edit/${raffle.id}`)}
                      data-testid={`edit-raffle-${raffle.id}`}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleToggleActive(raffle.id, raffle.is_active)}
                      data-testid={`toggle-raffle-${raffle.id}`}
                    >
                      {raffle.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="flex-1" data-testid={`delete-raffle-${raffle.id}`}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. Se eliminará permanentemente la rifa y todos sus datos.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(raffle.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RafflesList;
